import { Component, OnInit, inject, DestroyRef } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { catchError, first, of, switchMap, tap } from 'rxjs';
import { LegalDocument } from '@shared/entities/legalDocument';
import { LegalDocumentService } from '@shared/services/api/legal-document.service';
import { ThemeService } from 'src/app/services/theme.service';

@Component({
  selector: 'app-legal-document-form',
  templateUrl: './legal-document-form.component.html',
  styleUrl: './legal-document-form.component.scss'
})
export class LegalDocumentFormComponent implements OnInit {
  private readonly _destroyRef = inject(DestroyRef);
  private _formBuilder = inject(FormBuilder);
  private _route = inject(ActivatedRoute);
  private _router = inject(Router);
  private _location = inject(Location);
  private _legalDocumentService = inject(LegalDocumentService);
  private _themeService = inject(ThemeService);
  public translateService = inject(TranslateService);

  form!: FormGroup;
  private _id = '';
  isLight = true;
  isLoading = true;
  errorMessage = '';

  showSuccess = false;
  showError = false;
  saveSuccess = false;
  saveError = false;
  loadError = false;

  legalDocument: LegalDocument | null = null;
  pdf: File | null = null;

  ngOnInit(): void {
    this.form = this._formBuilder.group({
      htmlContent: ['']
    });

    this._themeService.refresh$
      .pipe(switchMap(() => this._themeService.isDarkTheme$.pipe(tap((isDarkTheme) => (this.isLight = !isDarkTheme)))))
      .subscribe();

    this._route.params
      .pipe(
        first(),
        takeUntilDestroyed(this._destroyRef),
        tap((params) => (this._id = params['id'] || ''))
      )
      .subscribe(() => this.loadLegalDocument());
  }

  private loadLegalDocument(): void {
    if (!this._id) {
      // The generic LOAD_ERROR_DESCRIPTION already covers this, so no extra detail line.
      this.setLoadError();
      return;
    }

    this.isLoading = true;
    this._legalDocumentService
      .find(this._id)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap((legalDocument) => {
          // find() swallows HTTP errors and emits null, so a missing document is the error case here.
          if (!legalDocument) {
            this.setLoadError();
            return;
          }

          this.legalDocument = legalDocument;
          this.form.patchValue({ htmlContent: legalDocument.htmlContent || '' });
          this.isLoading = false;
        }),
        catchError((error) => {
          this.setLoadError(this.extractErrorMessage(error));
          return of(null);
        })
      )
      .subscribe();
  }

  private setLoadError(message = ''): void {
    this.isLoading = false;
    this.showError = true;
    this.loadError = true;
    this.errorMessage = message;
  }

  onPdfSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.pdf = input.files[0];
    }
  }

  removePdf(): void {
    this.pdf = null;
  }

  onSubmit(): void {
    if (!this._id) return;

    this.isLoading = true;
    this.showError = false;
    this.saveError = false;

    this._legalDocumentService
      .update(this._id, this.form.value.htmlContent, this.pdf || undefined)
      .pipe(
        first(),
        takeUntilDestroyed(this._destroyRef),
        tap((legalDocument) => {
          this.isLoading = false;
          this.legalDocument = legalDocument;
          this.pdf = null;
          this.showSuccess = true;
          this.saveSuccess = true;
        }),
        catchError((error) => {
          this.isLoading = false;
          this.showError = true;
          this.saveError = true;
          this.errorMessage = this.extractErrorMessage(error);
          return of(null);
        })
      )
      .subscribe();
  }

  private extractErrorMessage(error: any): string {
    return error?.error?.errorMessage || error?.message || this.translateService.instant('LEGAL_DOCUMENTS_FORM.ERROR_UNKNOWN');
  }

  goToList(): void {
    this._router.navigate(['/legal-documents']);
  }

  goBack(): void {
    this._location.back();
  }

  tryAgain(): void {
    this.showError = false;
    this.saveError = false;

    if (this.loadError) {
      this.loadError = false;
      this.errorMessage = '';
      this.loadLegalDocument();
    }
  }
}
